import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiUsers, FiPlus, FiEdit, FiTrash2, FiShield, FiKey, FiX, FiSave, FiAward, FiTrendingUp, FiUserX, FiRotateCcw, FiAlertTriangle } from 'react-icons/fi';
import { getTeamMembers, createTeamMember, updateTeamMember, previewOffboard, applyOffboard, getAuthToken, getCareerTracks, assignCareerTrack, removeCareerTrack, getCertifications, createCertification, updateCertification, deleteCertification } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import TeamEmailConnect from '../components/TeamEmailConnect';
import CrewManager from '../components/CrewManager';
import DeviceManager from '../components/DeviceManager';
import { API_URL } from '../config';
import axios from 'axios';
import {
  CareerBadgeList,
  CAREER_TRACKS,
  TRACK_GROUPS,
  GroupBadge,
  DualTrackBadge,
  PayTierTable,
  getUserGroups,
  isDualTrack,
  formatTierBracket,
} from '../components/CareerBadge';

const BIRTH_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];

// Helper to get axios config with auth token
const getAuthConfig = async () => {
  const token = await getAuthToken();
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

// Standardized roles - used for permissions
const ROLES = [
  { value: 'Owner', label: 'Owner', description: 'Full system access' },
  { value: 'Office Manager', label: 'Office Manager', description: 'Payroll & admin access' },
  { value: 'Admin', label: 'Admin', description: 'Full system access' },
  { value: 'Office Assistant', label: 'Office Assistant', description: 'Office support & errands' },
  { value: 'Land Surveyor', label: 'Land Surveyor', description: 'Field work access' },
  { value: 'Survey Technician', label: 'Survey Technician', description: 'Field work access' },
  { value: 'CAD Technician', label: 'CAD Technician', description: 'Drafting access' },
  { value: 'Field Crew', label: 'Field Crew', description: 'Basic field access' },
];

// Roles with payroll management permissions
const PAYROLL_ROLES = ['Owner', 'Office Manager', 'Admin'];

function Team() {
  const { user, refreshUser } = useAuth();
  const [team, setTeam] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [offboardPlan, setOffboardPlan] = useState(null);
  const [offboardBusy, setOffboardBusy] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'Field Crew',
    hourly_rate: 0,
    hire_date: '',
    employment_type: 'Full-Time',
    vacation_adjustment: 0,
    sick_adjustment: 0,
    personal_adjustment: 0,
    username: '',
    password: ''
  });

  // Credentials modal state
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [credentialsMember, setCredentialsMember] = useState(null);
  const [credentialsForm, setCredentialsForm] = useState({
    username: '',
    password: '',
    confirmPassword: ''
  });
  const [credentialsError, setCredentialsError] = useState('');

  // Career & Certifications modal state
  const [showCareerModal, setShowCareerModal] = useState(false);
  const [careerMember, setCareerMember] = useState(null);
  const [careerTab, setCareerTab] = useState('tracks'); // 'tracks' or 'certs'
  const [memberTracks, setMemberTracks] = useState([]);
  const [memberCerts, setMemberCerts] = useState([]);
  const [trackForm, setTrackForm] = useState({ career_track: '', career_level: 1 });
  const [payPlacementForm, setPayPlacementForm] = useState({ pay_group: '', pay_tier: '', pay_bracket: '', tier_change_effective_at: '' });
  const [payPlacementSaving, setPayPlacementSaving] = useState(false);
  const [payPlacementMsg, setPayPlacementMsg] = useState('');
  const [certForm, setCertForm] = useState({ name: '', issuing_body: '', date_obtained: '', expiration_date: '', notes: '' });
  const [editingCert, setEditingCert] = useState(null);

  const CERT_PRESETS = ['NGS Field Safety L1', 'NGS Field Safety L2', 'Computer Literacy L1', 'Computer Literacy L2', 'First Aid/CPR', 'SIT', 'PLS', 'FAA Part 107', 'AutoCAD Certified', 'Carlson Certified', 'OSHA 10-Hour', 'OSHA 30-Hour'];

  // Check if user can manage team (has admin/payroll role)
  const canManageTeam = user?.can_manage_payroll || false;

  useEffect(() => {
    loadTeam();
  }, []);

  const loadTeam = async () => {
    try {
      // Team admin page is the one place that shows departed staff — greyed out, so
      // their history stays reachable without offering them on any assignment picker.
      const response = await getTeamMembers(true);
      setTeam(response.data.team_members);
    } catch (error) {
      console.error('Error loading team:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Double-check permissions
    if (!canManageTeam) {
      alert('You do not have permission to manage team members');
      return;
    }

    // Validate username and password for new members
    if (!editingMember) {
      if (!formData.username) {
        alert('Username is required');
        return;
      }
      if (!formData.password || formData.password.length < 4) {
        alert('Password must be at least 4 characters');
        return;
      }
    }
    
    try {
      if (editingMember) {
        // Don't send username/password on edit (use credentials modal for that)
        const { username, password, ...updateData } = formData;
        await updateTeamMember(editingMember.id, updateData);
      } else {
        await createTeamMember(formData);
      }
      setFormData({ name: '', email: '', phone: '', role: 'Field Crew', hourly_rate: 0, hire_date: '', employment_type: 'Full-Time', vacation_adjustment: 0, sick_adjustment: 0, personal_adjustment: 0, username: '', password: '' });
      setShowForm(false);
      setEditingMember(null);
      loadTeam();
    } catch (error) {
      console.error('Error saving team member:', error);
      alert(error.response?.data?.error || 'Failed to save team member');
    }
  };

  const handleEdit = (member) => {
    if (!canManageTeam) return;
    setEditingMember(member);
    setFormData({
      name: member.name,
      email: member.email || '',
      phone: member.phone || '',
      role: member.role || 'Field Crew',
      hourly_rate: member.hourly_rate || 0,
      hire_date: member.hire_date || '',
      birth_month: member.birth_month || '',
      birth_day: member.birth_day || '',
      employment_type: member.employment_type || 'Full-Time',
      vacation_adjustment: member.vacation_adjustment || 0,
      sick_adjustment: member.sick_adjustment || 0,
      personal_adjustment: member.personal_adjustment || 0,
      holiday_pay_eligible: !!member.holiday_pay_eligible,
      holiday_pay_hours: member.holiday_pay_hours != null ? member.holiday_pay_hours : 10,
      can_manage_schedule: !!member.can_manage_schedule,
      can_manage_quotes: !!member.can_manage_quotes,
      can_view_call_recordings: !!member.can_view_call_recordings,
      can_view_daily_summary: !!member.can_view_daily_summary,
      username: '',
      password: ''
    });
    setShowForm(true);
  };

  // Offboarding runs in two steps: ask the server what WOULD change, show it, then apply.
  // The old button here called DELETE /api/team/<id>, which tried to hard-delete the row
  // and always failed on a NOT NULL child constraint — nobody was ever deactivated by it.
  const handleOffboard = async (member) => {
    if (!canManageTeam) return;
    setOffboardBusy(true);
    try {
      const response = await previewOffboard(member.id);
      setOffboardPlan({ member, plan: response.data });
    } catch (error) {
      console.error('Error previewing offboard:', error);
      alert(error.response?.data?.error || 'Could not load the offboarding summary.');
    } finally {
      setOffboardBusy(false);
    }
  };

  const confirmOffboard = async () => {
    if (!offboardPlan) return;
    setOffboardBusy(true);
    try {
      const response = await applyOffboard(offboardPlan.member.id);
      const revoked = response.data.revoked_tokens || 0;
      const unassigned = (response.data.will_unassign_open_jobs || []).length;
      setOffboardPlan(null);
      loadTeam();
      alert(`${offboardPlan.member.name} has been offboarded.\n\n` +
            `Logins revoked: ${revoked}\n` +
            `Open jobs unassigned: ${unassigned}\n\n` +
            `Their time entries, job notes and history are preserved.`);
    } catch (error) {
      console.error('Error offboarding team member:', error);
      alert(error.response?.data?.error || 'Failed to offboard team member');
    } finally {
      setOffboardBusy(false);
    }
  };

  // Deliberately NOT the reverse of offboarding — this restores access only. Crew
  // membership and job assignments were ended on purpose and are re-added by hand.
  const handleReactivate = async (member) => {
    if (!canManageTeam) return;
    if (!window.confirm(`Reactivate ${member.name}?\n\nThis restores their access. It does not put them back on their old crew or jobs.`)) return;
    try {
      await updateTeamMember(member.id, { is_active: true });
      loadTeam();
    } catch (error) {
      console.error('Error reactivating team member:', error);
      alert(error.response?.data?.error || 'Failed to reactivate team member');
    }
  };

  const cancelEdit = () => {
    setShowForm(false);
    setEditingMember(null);
    setFormData({ name: '', email: '', phone: '', role: 'Field Crew', hourly_rate: 0, hire_date: '', employment_type: 'Full-Time', vacation_adjustment: 0, sick_adjustment: 0, personal_adjustment: 0, username: '', password: '' });
  };

  const openCredentialsModal = (member) => {
    setCredentialsMember(member);
    setCredentialsForm({
      username: member.username || '',
      password: '',
      confirmPassword: ''
    });
    setCredentialsError('');
    setShowCredentialsModal(true);
  };

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setCredentialsError('');

    // Validate
    if (credentialsForm.password && credentialsForm.password !== credentialsForm.confirmPassword) {
      setCredentialsError('Passwords do not match');
      return;
    }

    if (credentialsForm.password && credentialsForm.password.length < 4) {
      setCredentialsError('Password must be at least 4 characters');
      return;
    }

    try {
      const updateData = {};
      if (credentialsForm.username) {
        updateData.username = credentialsForm.username;
      }
      if (credentialsForm.password) {
        updateData.password = credentialsForm.password;
      }

      const config = await getAuthConfig();
      await axios.put(`${API_URL}/team/${credentialsMember.id}/credentials`, updateData, config);
      
      setShowCredentialsModal(false);
      setCredentialsMember(null);
      loadTeam();
      alert('Credentials updated successfully');
    } catch (error) {
      console.error('Error updating credentials:', error);
      setCredentialsError(error.response?.data?.error || 'Failed to update credentials');
    }
  };

  const hasPayrollAccess = (role) => PAYROLL_ROLES.includes(role);

  const openCareerModal = async (member) => {
    setCareerMember(member);
    setCareerTab('tracks');
    setTrackForm({ career_track: '', career_level: 1 });
    setPayPlacementForm({
      pay_group: member.pay_group || '',
      pay_tier: member.pay_tier ? String(member.pay_tier) : '',
      pay_bracket: member.pay_bracket || '',
    });
    setPayPlacementMsg('');
    setCertForm({ name: '', issuing_body: '', date_obtained: '', expiration_date: '', notes: '' });
    setEditingCert(null);
    setShowCareerModal(true);
    try {
      const [tracksRes, certsRes] = await Promise.all([
        getCareerTracks(member.id),
        getCertifications(member.id)
      ]);
      setMemberTracks(tracksRes.data);
      setMemberCerts(certsRes.data);
    } catch (err) {
      console.error('Error loading career data:', err);
    }
  };

  const handleAssignTrack = async (e) => {
    e.preventDefault();
    if (!trackForm.career_track) return;
    try {
      await assignCareerTrack({ member_id: careerMember.id, career_track: trackForm.career_track, career_level: trackForm.career_level });
      const res = await getCareerTracks(careerMember.id);
      setMemberTracks(res.data);
      setTrackForm({ career_track: '', career_level: 1 });
      loadTeam();
      if (careerMember.id === user?.id) refreshUser();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to assign track');
    }
  };

  const handleRemoveTrack = async (trackId) => {
    if (!window.confirm('Remove this career track?')) return;
    try {
      await removeCareerTrack(trackId);
      const res = await getCareerTracks(careerMember.id);
      setMemberTracks(res.data);
      loadTeam();
      if (careerMember.id === user?.id) refreshUser();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to remove track');
    }
  };

  const handleSavePayPlacement = async (e) => {
    e.preventDefault();
    setPayPlacementSaving(true);
    setPayPlacementMsg('');
    try {
      const payload = {
        pay_group: payPlacementForm.pay_group || null,
        pay_tier: payPlacementForm.pay_tier === '' ? null : parseInt(payPlacementForm.pay_tier, 10),
        pay_bracket: payPlacementForm.pay_bracket || null,
      };
      if (payPlacementForm.tier_change_effective_at) {
        payload.tier_change_effective_at = payPlacementForm.tier_change_effective_at;
      }
      await updateTeamMember(careerMember.id, payload);
      // Refresh local careerMember state with the saved values so the form sees the truth.
      setCareerMember({ ...careerMember, ...payload });
      loadTeam();
      if (careerMember.id === user?.id) refreshUser();
      setPayPlacementMsg('Pay placement saved.');
      // Clear the message after a few seconds
      setTimeout(() => setPayPlacementMsg(''), 3000);
    } catch (err) {
      setPayPlacementMsg(err.response?.data?.error || 'Failed to save pay placement.');
    } finally {
      setPayPlacementSaving(false);
    }
  };

  const handleSaveCert = async (e) => {
    e.preventDefault();
    try {
      if (editingCert) {
        await updateCertification(editingCert.id, { ...certForm });
      } else {
        await createCertification({ member_id: careerMember.id, ...certForm });
      }
      const res = await getCertifications(careerMember.id);
      setMemberCerts(res.data);
      setCertForm({ name: '', issuing_body: '', date_obtained: '', expiration_date: '', notes: '' });
      setEditingCert(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save certification');
    }
  };

  const handleEditCert = (cert) => {
    setEditingCert(cert);
    setCertForm({
      name: cert.name,
      issuing_body: cert.issuing_body || '',
      date_obtained: cert.date_obtained || '',
      expiration_date: cert.expiration_date || '',
      notes: cert.notes || ''
    });
  };

  const handleDeleteCert = async (certId) => {
    if (!window.confirm('Delete this certification?')) return;
    try {
      await deleteCertification(certId);
      const res = await getCertifications(careerMember.id);
      setMemberCerts(res.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete certification');
    }
  };

  const getCertStatusColor = (status) => {
    if (status === 'Active') return { bg: '#e8f5e9', color: '#2e7d32' };
    if (status === 'Expiring Soon') return { bg: '#fff3e0', color: '#e65100' };
    return { bg: '#ffebee', color: '#c62828' };
  };

  return (
    <div className="team-page">
      {/* Credentials Modal */}
      {showCredentialsModal && credentialsMember && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '25px',
            width: '100%',
            maxWidth: '450px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FiKey /> Login Credentials
              </h2>
              <button 
                onClick={() => setShowCredentialsModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px' }}
              >
                <FiX />
              </button>
            </div>

            <p style={{ color: '#666', marginBottom: '20px' }}>
              Update login credentials for <strong>{credentialsMember.name}</strong>
            </p>

            {credentialsError && (
              <div style={{
                backgroundColor: '#ffebee',
                color: '#c62828',
                padding: '10px 15px',
                borderRadius: '6px',
                marginBottom: '15px'
              }}>
                {credentialsError}
              </div>
            )}

            <form onSubmit={handleCredentialsSubmit}>
              <div className="form-group" style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Username</label>
                <input
                  type="text"
                  value={credentialsForm.username}
                  onChange={(e) => setCredentialsForm({...credentialsForm, username: e.target.value})}
                  className="form-control"
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
                  placeholder="Enter username"
                />
              </div>

              <div className="form-group" style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>New Password</label>
                <input
                  type="password"
                  value={credentialsForm.password}
                  onChange={(e) => setCredentialsForm({...credentialsForm, password: e.target.value})}
                  className="form-control"
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
                  placeholder="Leave blank to keep current"
                />
              </div>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Confirm Password</label>
                <input
                  type="password"
                  value={credentialsForm.confirmPassword}
                  onChange={(e) => setCredentialsForm({...credentialsForm, confirmPassword: e.target.value})}
                  className="form-control"
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
                  placeholder="Confirm new password"
                />
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => setShowCredentialsModal(false)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: '#f5f5f5',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: '#1976d2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  <FiSave /> Save Credentials
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Career & Certifications Modal */}
      {showCareerModal && careerMember && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '25px',
            width: '100%', maxWidth: '600px', maxHeight: '85vh', overflowY: 'auto',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FiAward /> {careerMember.name}
              </h2>
              <button onClick={() => setShowCareerModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px' }}>
                <FiX />
              </button>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: '0', marginBottom: '20px', borderBottom: '2px solid #eee' }}>
              {['tracks', 'certs'].map(tab => (
                <button key={tab} onClick={() => setCareerTab(tab)} style={{
                  padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer',
                  fontWeight: careerTab === tab ? '600' : '400',
                  color: careerTab === tab ? '#1976d2' : '#666',
                  borderBottom: careerTab === tab ? '2px solid #1976d2' : '2px solid transparent',
                  marginBottom: '-2px', fontSize: '14px'
                }}>
                  {tab === 'tracks' ? 'Career Tracks' : 'Certifications'}
                </button>
              ))}
            </div>

            {/* Career Tracks Tab */}
            {careerTab === 'tracks' && (
              <div>
                {/* Group memberships + Dual-Track Bracket eligibility */}
                {memberTracks.length > 0 && (
                  <div style={{
                    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px',
                    padding: '10px 12px', marginBottom: '12px',
                    backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '8px'
                  }}>
                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Groups:
                    </span>
                    {getUserGroups(memberTracks).map(g => (
                      <GroupBadge key={g} group={g} />
                    ))}
                    {isDualTrack(memberTracks) && <DualTrackBadge />}
                  </div>
                )}

                {/* Current tracks (no per-track pay band — pay is by tier within group) */}
                {memberTracks.length > 0 ? (
                  <div style={{ marginBottom: '20px' }}>
                    {memberTracks.map(t => {
                      const group = TRACK_GROUPS[t.career_track];
                      return (
                        <div key={t.id} style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '10px 12px', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '8px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            <CareerBadgeList tracks={[t]} />
                            {group && group !== 'Licensed' && <GroupBadge group={group} />}
                          </div>
                          <button onClick={() => handleRemoveTrack(t.id)} style={{
                            background: 'none', border: 'none', cursor: 'pointer', color: '#f44336', padding: '4px'
                          }} title="Remove track">
                            <FiTrash2 size={16} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p style={{ color: '#999', fontStyle: 'italic', marginBottom: '20px' }}>No career tracks assigned yet.</p>
                )}

                {/* Pay placement card — admin-editable; shows current tier/bracket + effective rate */}
                {(() => {
                  const currentRate = formatTierBracket(
                    careerMember.pay_group,
                    careerMember.pay_tier,
                    careerMember.pay_bracket
                  );
                  const isEligibleDualTrack = isDualTrack(memberTracks);
                  const groupForTable =
                    payPlacementForm.pay_group ||
                    careerMember.pay_group ||
                    getUserGroups(memberTracks)[0] ||
                    null;
                  const previewRate = formatTierBracket(
                    payPlacementForm.pay_group,
                    payPlacementForm.pay_tier ? parseInt(payPlacementForm.pay_tier, 10) : null,
                    payPlacementForm.pay_bracket
                  );
                  const recommendsSenior =
                    isEligibleDualTrack &&
                    payPlacementForm.pay_bracket &&
                    payPlacementForm.pay_bracket !== 'Senior';
                  return (
                    <div style={{
                      padding: '14px', marginBottom: '20px',
                      backgroundColor: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: '8px' }}>
                        <div>
                          <h4 style={{ margin: '0 0 4px', fontSize: '14px', color: '#9a3412' }}>Pay Placement</h4>
                          <div style={{ fontSize: '12px', color: '#7c2d12' }}>
                            Tier within group (Entry / Mid / Senior). Set by admin during reviews.
                          </div>
                        </div>
                        <div style={{ fontSize: '13px', color: '#7c2d12' }}>
                          Current placement: {careerMember.pay_group && careerMember.pay_tier && careerMember.pay_bracket ? (
                            <strong>
                              {careerMember.pay_group} Tier {careerMember.pay_tier} {careerMember.pay_bracket}
                              {currentRate ? ` — ${currentRate}` : ''}
                            </strong>
                          ) : <em>not set</em>}
                        </div>
                      </div>

                      {canManageTeam ? (
                        <form onSubmit={handleSavePayPlacement} style={{ marginTop: '10px' }}>
                          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                            <div style={{ flex: 1, minWidth: 110 }}>
                              <label style={{ display: 'block', fontSize: '11px', marginBottom: '4px', color: '#7c2d12', fontWeight: 600 }}>Group</label>
                              <select
                                value={payPlacementForm.pay_group}
                                onChange={(e) => setPayPlacementForm({ ...payPlacementForm, pay_group: e.target.value })}
                                style={{ padding: '6px', width: '100%', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                              >
                                <option value="">—</option>
                                <option value="Field">Field</option>
                                <option value="Office">Office</option>
                              </select>
                            </div>
                            <div style={{ flex: 1, minWidth: 80 }}>
                              <label style={{ display: 'block', fontSize: '11px', marginBottom: '4px', color: '#7c2d12', fontWeight: 600 }}>Tier</label>
                              <select
                                value={payPlacementForm.pay_tier}
                                onChange={(e) => setPayPlacementForm({ ...payPlacementForm, pay_tier: e.target.value })}
                                style={{ padding: '6px', width: '100%', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                              >
                                <option value="">—</option>
                                {[1,2,3,4,5,6].map(t => <option key={t} value={t}>{t}</option>)}
                              </select>
                            </div>
                            <div style={{ flex: 1, minWidth: 110 }}>
                              <label style={{ display: 'block', fontSize: '11px', marginBottom: '4px', color: '#7c2d12', fontWeight: 600 }}>Bracket</label>
                              <select
                                value={payPlacementForm.pay_bracket}
                                onChange={(e) => setPayPlacementForm({ ...payPlacementForm, pay_bracket: e.target.value })}
                                style={{ padding: '6px', width: '100%', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                              >
                                <option value="">—</option>
                                <option value="Entry">Entry</option>
                                <option value="Mid">Mid</option>
                                <option value="Senior">Senior</option>
                              </select>
                            </div>
                            <div style={{ flex: 1, minWidth: 140 }}>
                              <label style={{ display: 'block', fontSize: '11px', marginBottom: '4px', color: '#7c2d12', fontWeight: 600 }} title="Backdate the placement — leave empty for today. Used when migrating an existing tech onto the framework so time-in-tier reflects their actual history.">
                                Effective from
                              </label>
                              <input
                                type="date"
                                value={payPlacementForm.tier_change_effective_at}
                                onChange={(e) => setPayPlacementForm({ ...payPlacementForm, tier_change_effective_at: e.target.value })}
                                max={new Date().toISOString().slice(0, 10)}
                                style={{ padding: '6px', width: '100%', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                              />
                            </div>
                            <button
                              type="submit"
                              disabled={payPlacementSaving}
                              style={{
                                padding: '8px 16px', backgroundColor: '#c2410c', color: 'white',
                                border: 'none', borderRadius: '6px', cursor: payPlacementSaving ? 'wait' : 'pointer', fontWeight: 600
                              }}
                            >
                              {payPlacementSaving ? 'Saving…' : 'Save'}
                            </button>
                          </div>
                          {previewRate && (
                            <div style={{ marginTop: '8px', fontSize: '12px', color: '#7c2d12' }}>
                              Selected placement rate (advisory): <strong>{previewRate}</strong>
                              {careerMember.hourly_rate != null && (
                                <> — current actual hourly rate on record: <strong>${Number(careerMember.hourly_rate).toFixed(2)}/hr</strong>
                                {Math.abs(parseFloat(previewRate.replace(/[^0-9.]/g, '')) - careerMember.hourly_rate) > 0.005 && (
                                  <span style={{ color: '#b91c1c', marginLeft: 4 }}>(differs — update on the member's edit form if intended)</span>
                                )}</>
                              )}
                            </div>
                          )}
                          {recommendsSenior && (
                            <div style={{ marginTop: '8px', fontSize: '12px', color: '#0d6b3b', fontWeight: 600 }}>
                              ★ Dual-Track Bracket Rule: this member holds certs in both Field and Office groups — recommend Senior bracket at this tier.
                            </div>
                          )}
                          {payPlacementMsg && (
                            <div style={{ marginTop: '8px', fontSize: '12px', color: payPlacementMsg.includes('saved') ? '#166534' : '#b91c1c' }}>
                              {payPlacementMsg}
                            </div>
                          )}
                        </form>
                      ) : (
                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#7c2d12', fontStyle: 'italic' }}>
                          Pay placement is set by admin / payroll managers.
                        </div>
                      )}

                      {groupForTable && (
                        <div style={{ marginTop: '14px' }}>
                          <PayTierTable
                            group={groupForTable}
                            highlightTier={
                              payPlacementForm.pay_tier
                                ? parseInt(payPlacementForm.pay_tier, 10)
                                : careerMember.pay_tier
                            }
                            highlightBracket={
                              payPlacementForm.pay_bracket || careerMember.pay_bracket
                            }
                          />
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Cross-group reference table if user is in both groups */}
                {memberTracks.length > 0 && getUserGroups(memberTracks).length > 1 && (
                  <div style={{ marginBottom: '20px' }}>
                    {getUserGroups(memberTracks)
                      .filter(g => g !== (payPlacementForm.pay_group || careerMember.pay_group))
                      .map(g => <PayTierTable key={g} group={g} />)
                    }
                    <div style={{ fontSize: '11px', color: '#64748b', marginTop: '6px', lineHeight: 1.5 }}>
                      This member holds certs in both Field and Office groups — under the Dual-Track Bracket Rule,
                      they qualify for the Senior bracket at their current tier (recommended above when applicable).
                    </div>
                  </div>
                )}

                {/* Add track form */}
                <form onSubmit={handleAssignTrack} style={{
                  padding: '15px', backgroundColor: '#f0f7ff', borderRadius: '8px', border: '1px solid #bbdefb'
                }}>
                  <h4 style={{ margin: '0 0 10px', fontSize: '14px', color: '#1976d2' }}>Add / Update Track</h4>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div style={{ flex: 2 }}>
                      <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>Career Track</label>
                      <select value={trackForm.career_track} onChange={(e) => setTrackForm({...trackForm, career_track: e.target.value})}
                        className="form-control" style={{ padding: '8px' }}>
                        <option value="">Select track...</option>
                        {Object.keys(CAREER_TRACKS).map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>Level</label>
                      <select value={trackForm.career_level} onChange={(e) => setTrackForm({...trackForm, career_level: parseInt(e.target.value)})}
                        className="form-control" style={{ padding: '8px' }}>
                        <option value={1}>Level 1</option>
                        <option value={2}>Level 2</option>
                        <option value={3}>Level 3</option>
                      </select>
                    </div>
                    <button type="submit" style={{
                      padding: '8px 16px', backgroundColor: '#1976d2', color: 'white',
                      border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500'
                    }}>Assign</button>
                  </div>
                  {trackForm.career_track && TRACK_GROUPS[trackForm.career_track] && TRACK_GROUPS[trackForm.career_track] !== 'Licensed' && (
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#475569' }}>
                      <strong>{trackForm.career_track}</strong> is in the <em>{TRACK_GROUPS[trackForm.career_track]} Surveyor Group</em>.
                      Pay is by tier within group (not by track-level) — tier/bracket is assigned separately.
                    </div>
                  )}
                </form>
              </div>
            )}

            {/* Certifications Tab */}
            {careerTab === 'certs' && (
              <div>
                {/* Current certs */}
                {memberCerts.length > 0 ? (
                  <div style={{ marginBottom: '20px' }}>
                    {memberCerts.map(c => {
                      const statusStyle = getCertStatusColor(c.status);
                      return (
                        <div key={c.id} style={{
                          padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '8px'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                              <div style={{ fontWeight: '600', fontSize: '14px' }}>{c.name}</div>
                              {c.issuing_body && <div style={{ fontSize: '12px', color: '#666' }}>{c.issuing_body}</div>}
                              <div style={{ display: 'flex', gap: '10px', marginTop: '4px', fontSize: '12px', color: '#888' }}>
                                {c.date_obtained && <span>Obtained: {new Date(c.date_obtained + 'T00:00:00').toLocaleDateString()}</span>}
                                {c.expiration_date && <span>Expires: {new Date(c.expiration_date + 'T00:00:00').toLocaleDateString()}</span>}
                              </div>
                              {c.notes && <div style={{ fontSize: '12px', color: '#666', marginTop: '4px', fontStyle: 'italic' }}>{c.notes}</div>}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{
                                padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
                                backgroundColor: statusStyle.bg, color: statusStyle.color
                              }}>{c.status}</span>
                              <button onClick={() => handleEditCert(c)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1976d2' }}>
                                <FiEdit size={14} />
                              </button>
                              <button onClick={() => handleDeleteCert(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#f44336' }}>
                                <FiTrash2 size={14} />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p style={{ color: '#999', fontStyle: 'italic', marginBottom: '20px' }}>No certifications recorded yet.</p>
                )}

                {/* Add/Edit cert form */}
                <form onSubmit={handleSaveCert} style={{
                  padding: '15px', backgroundColor: '#f0f7ff', borderRadius: '8px', border: '1px solid #bbdefb'
                }}>
                  <h4 style={{ margin: '0 0 10px', fontSize: '14px', color: '#1976d2' }}>
                    {editingCert ? 'Edit Certification' : 'Add Certification'}
                  </h4>
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>Certification Name *</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input type="text" value={certForm.name} onChange={(e) => setCertForm({...certForm, name: e.target.value})}
                        required className="form-control" style={{ flex: 1, padding: '8px' }} placeholder="e.g., NGS Field Safety L1" />
                      <select onChange={(e) => { if (e.target.value) setCertForm({...certForm, name: e.target.value}); e.target.value = ''; }}
                        className="form-control" style={{ width: 'auto', padding: '8px' }}>
                        <option value="">Presets</option>
                        {CERT_PRESETS.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>Issuing Body</label>
                      <input type="text" value={certForm.issuing_body} onChange={(e) => setCertForm({...certForm, issuing_body: e.target.value})}
                        className="form-control" style={{ padding: '8px' }} placeholder="e.g., NGS" />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>Date Obtained</label>
                      <input type="date" value={certForm.date_obtained} onChange={(e) => setCertForm({...certForm, date_obtained: e.target.value})}
                        className="form-control" style={{ padding: '8px' }} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>Expiration Date</label>
                      <input type="date" value={certForm.expiration_date} onChange={(e) => setCertForm({...certForm, expiration_date: e.target.value})}
                        className="form-control" style={{ padding: '8px' }} />
                      <small style={{ color: '#999' }}>Leave blank if no expiration</small>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>Notes</label>
                      <input type="text" value={certForm.notes} onChange={(e) => setCertForm({...certForm, notes: e.target.value})}
                        className="form-control" style={{ padding: '8px' }} placeholder="Optional notes" />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button type="submit" style={{
                      padding: '8px 16px', backgroundColor: '#1976d2', color: 'white',
                      border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500'
                    }}>{editingCert ? 'Update' : 'Add'} Certification</button>
                    {editingCert && (
                      <button type="button" onClick={() => {
                        setEditingCert(null);
                        setCertForm({ name: '', issuing_body: '', date_obtained: '', expiration_date: '', notes: '' });
                      }} style={{
                        padding: '8px 16px', backgroundColor: '#f5f5f5',
                        border: 'none', borderRadius: '6px', cursor: 'pointer'
                      }}>Cancel</button>
                    )}
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="page-header">
        <div>
          <h1><FiUsers /> Team Members</h1>
          <p className="subtitle">Manage your surveying team</p>
        </div>
        {canManageTeam && (
          <div style={{ display: 'flex', gap: 8 }}>
            <Link to="/team/career-roster" className="btn btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}>
              <FiTrendingUp /> Career Roster
            </Link>
            <button className="btn btn-primary" onClick={() => {
              cancelEdit();
              setShowForm(!showForm);
            }}>
              <FiPlus /> {showForm ? 'Cancel' : 'Add Team Member'}
            </button>
          </div>
        )}
      </div>

      {/* Field crews. Sits above the roster because it's the thing that drives the
          Command Center board, and a chief change here changes what the wall shows. */}
      {canManageTeam && <CrewManager team={team} />}

      {/* Data collectors, directly under the crews they're carried by — a collector is
          only meaningful once it's assigned to one. */}
      {canManageTeam && <DeviceManager />}

      {/* Connect each member's company mailbox (Owner/Admin only — the endpoint 403s
          for everyone else, and the panel hides itself). */}
      <TeamEmailConnect />

      {/* Only show form for users with admin permissions */}
      {showForm && canManageTeam && (
        <div className="form-card" style={{ marginBottom: '20px', padding: '20px', backgroundColor: 'white', borderRadius: '8px' }}>
          <h2>{editingMember ? 'Edit Team Member' : 'Add Team Member'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label>Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className="form-control"
                  required
                >
                  {ROLES.map(role => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
                {hasPayrollAccess(formData.role) && (
                  <small style={{ color: '#2196f3', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                    <FiShield /> This role has payroll management access
                  </small>
                )}
              </div>
            </div>

            {/* Username and Password - only for new members */}
            {!editingMember && (
              <div className="form-row" style={{ 
                backgroundColor: '#f0f7ff', 
                padding: '15px', 
                borderRadius: '8px', 
                marginBottom: '15px',
                border: '1px solid #bbdefb'
              }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label><FiKey style={{ marginRight: '5px' }} />Username *</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({...formData, username: e.target.value})}
                    required
                    className="form-control"
                    placeholder="Login username"
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Password *</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    required
                    className="form-control"
                    placeholder="Min 4 characters"
                    minLength={4}
                  />
                </div>
              </div>
            )}

            {editingMember && (
              <p style={{ 
                color: '#666', 
                fontSize: '13px', 
                backgroundColor: '#f5f5f5', 
                padding: '10px', 
                borderRadius: '6px',
                marginBottom: '15px'
              }}>
                <FiKey style={{ marginRight: '5px' }} />
                To change login credentials, use the key icon on the team member card.
              </p>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="form-control"
                />
              </div>
              {canManageTeam && (
                <div className="form-group">
                  <label>Hourly Rate ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.hourly_rate}
                    onChange={(e) => setFormData({...formData, hourly_rate: parseFloat(e.target.value) || 0})}
                    className="form-control"
                  />
                </div>
              )}
              {canManageTeam && (
                <div className="form-group">
                  <label>Hire Date</label>
                  <input
                    type="date"
                    value={formData.hire_date}
                    onChange={(e) => setFormData({...formData, hire_date: e.target.value})}
                    className="form-control"
                  />
                </div>
              )}
              {canManageTeam && editingMember && (
                <div className="form-group">
                  <label>Birthday</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <select
                      value={formData.birth_month}
                      onChange={(e) => setFormData({...formData, birth_month: e.target.value, birth_day: e.target.value && Number(formData.birth_day) <= new Date(2000, Number(e.target.value), 0).getDate() ? formData.birth_day : ''})}
                      className="form-control"
                    >
                      <option value="">Month</option>
                      {BIRTH_MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                    </select>
                    <select
                      value={formData.birth_day}
                      onChange={(e) => setFormData({...formData, birth_day: e.target.value})}
                      className="form-control"
                      style={{ maxWidth: '90px' }}
                    >
                      <option value="">Day</option>
                      {Array.from({ length: formData.birth_month ? new Date(2000, Number(formData.birth_month), 0).getDate() : 31 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>{i + 1}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              {canManageTeam && (
                <div className="form-group">
                  <label>Employment Type</label>
                  <select
                    value={formData.employment_type}
                    onChange={(e) => setFormData({...formData, employment_type: e.target.value})}
                    className="form-control"
                  >
                    <option value="Full-Time">Full-Time</option>
                    <option value="Part-Time">Part-Time</option>
                    <option value="Temporary">Temporary</option>
                  </select>
                </div>
              )}
            </div>

            {/* Holiday Pay — only when editing existing members */}
            {canManageTeam && editingMember && (
              <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#fffbeb', borderRadius: '8px', border: '1px solid #fcd34d' }}>
                <h4 style={{ margin: '0 0 10px', fontSize: '14px', color: '#92400e' }}>Holiday Pay</h4>
                <p style={{ fontSize: '12px', color: '#666', margin: '0 0 15px', fontStyle: 'italic' }}>
                  Eligible employees get paid for every company holiday in the pay period. Hours per holiday default to 10 (4x10 schedule); half-day holidays automatically pay half. Manage holidays at Holidays in the left nav.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', alignItems: 'end' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={!!formData.holiday_pay_eligible}
                        onChange={(e) => setFormData({ ...formData, holiday_pay_eligible: e.target.checked })}
                        style={{ width: '16px', height: '16px' }}
                      />
                      Eligible for paid holidays
                    </label>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: '13px' }}>Hours per full holiday</label>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      max="24"
                      value={formData.holiday_pay_hours ?? 10}
                      onChange={(e) => setFormData({ ...formData, holiday_pay_hours: parseFloat(e.target.value) || 0 })}
                      className="form-control"
                      disabled={!formData.holiday_pay_eligible}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Permissions — only when editing existing members */}
            {canManageTeam && editingMember && (
              <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f5f3ff', borderRadius: '8px', border: '1px solid #ddd6fe' }}>
                <h4 style={{ margin: '0 0 10px', fontSize: '14px', color: '#5b21b6' }}>Permissions</h4>
                <p style={{ fontSize: '12px', color: '#666', margin: '0 0 12px', fontStyle: 'italic' }}>
                  Admins, Owners, and Office Managers always have these permissions. Use these to grant rights to other roles (e.g., crew chiefs) without giving full payroll/admin access.
                </p>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', marginBottom: '8px' }}>
                  <input
                    type="checkbox"
                    checked={!!formData.can_manage_schedule}
                    onChange={(e) => setFormData({ ...formData, can_manage_schedule: e.target.checked })}
                    style={{ width: '16px', height: '16px' }}
                  />
                  Can edit the schedule (move jobs, change priority)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', marginBottom: '8px' }}>
                  <input
                    type="checkbox"
                    checked={!!formData.can_manage_quotes}
                    onChange={(e) => setFormData({ ...formData, can_manage_quotes: e.target.checked })}
                    style={{ width: '16px', height: '16px' }}
                  />
                  Can manage quotes &amp; quote requests
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                  <input
                    type="checkbox"
                    checked={!!formData.can_view_call_recordings}
                    onChange={(e) => setFormData({ ...formData, can_view_call_recordings: e.target.checked })}
                    style={{ width: '16px', height: '16px' }}
                  />
                  Can view &amp; listen to call recordings
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                  <input
                    type="checkbox"
                    checked={!!formData.can_view_daily_summary}
                    onChange={(e) => setFormData({ ...formData, can_view_daily_summary: e.target.checked })}
                    style={{ width: '16px', height: '16px' }}
                  />
                  Can view the Daily Summary
                </label>
              </div>
            )}

            {/* Time Off Adjustments - Only show for editing existing members */}
            {canManageTeam && editingMember && (
              <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                <h4 style={{ margin: '0 0 10px', fontSize: '14px', color: '#0369a1' }}>
                  Time Off Adjustments
                </h4>
                <p style={{ fontSize: '12px', color: '#666', margin: '0 0 15px', fontStyle: 'italic' }}>
                  Enter days already used before system tracking. These subtract from their entitlement and auto-reset on their anniversary (vacation) or Jan 1st (sick/personal).
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: '13px' }}>Vacation Used</label>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      value={formData.vacation_adjustment}
                      onChange={(e) => setFormData({...formData, vacation_adjustment: parseFloat(e.target.value) || 0})}
                      className="form-control"
                      style={{ textAlign: 'center' }}
                    />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: '13px' }}>Sick Used</label>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      value={formData.sick_adjustment}
                      onChange={(e) => setFormData({...formData, sick_adjustment: parseFloat(e.target.value) || 0})}
                      className="form-control"
                      style={{ textAlign: 'center' }}
                    />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: '13px' }}>Personal Used</label>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      value={formData.personal_adjustment}
                      onChange={(e) => setFormData({...formData, personal_adjustment: parseFloat(e.target.value) || 0})}
                      className="form-control"
                      style={{ textAlign: 'center' }}
                    />
                  </div>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
              <button type="submit" className="btn btn-primary">
                {editingMember ? 'Save Changes' : 'Add Team Member'}
              </button>
              {editingMember && (
                <button type="button" className="btn btn-secondary" onClick={cancelEdit}>
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
      )}

      {/* Role Legend */}
      <div style={{
        backgroundColor: '#f8f9fa',
        padding: '15px',
        borderRadius: '8px',
        marginBottom: '20px'
      }}>
        <h4 style={{ margin: '0 0 10px', fontSize: '14px', color: '#666' }}>
          <FiShield style={{ marginRight: '5px' }} /> Role Permissions
        </h4>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', fontSize: '13px' }}>
          <span><strong>Owner / Office Manager / Admin:</strong> Can approve timesheets, edit time entries, manage team & payroll</span>
          <span><strong>Other roles:</strong> Can clock in/out and view own timesheets</span>
        </div>
      </div>

      {offboardPlan && (() => {
        const { member, plan } = offboardPlan;
        const jobs = plan.will_unassign_open_jobs || [];
        const crews = plan.will_end_crew_membership || [];
        const blocking = plan.blocking || [];
        const row = { margin: '4px 0', fontSize: '14px' };
        return (
          <div style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'
          }}>
            <div style={{
              backgroundColor: 'white', borderRadius: '8px', padding: '25px',
              maxWidth: '620px', width: '100%', maxHeight: '85vh', overflowY: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h2 style={{ margin: 0, fontSize: '20px' }}>Offboard {member.name}</h2>
                <button onClick={() => setOffboardPlan(null)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px' }}>
                  <FiX />
                </button>
              </div>

              {blocking.length > 0 ? (
                <div style={{ backgroundColor: '#fff3e0', border: '1px solid #ffb74d', borderRadius: '6px', padding: '15px' }}>
                  <p style={{ margin: '0 0 8px', fontWeight: 600, color: '#e65100', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FiAlertTriangle /> Resolve this first
                  </p>
                  {blocking.map((b, i) => (
                    <p key={i} style={{ ...row, color: '#e65100' }}>• {b}</p>
                  ))}
                  <p style={{ ...row, color: '#e65100', marginTop: '10px' }}>
                    An open time entry can never be closed once they lose access, so payroll has to settle it by hand.
                  </p>
                </div>
              ) : (
                <>
                  <p style={{ color: '#666', fontSize: '14px', marginTop: 0 }}>
                    Nothing has changed yet. This is what will happen:
                  </p>

                  <div style={{ backgroundColor: '#fff5f5', border: '1px solid #ffcdd2', borderRadius: '6px', padding: '15px', marginBottom: '15px' }}>
                    <p style={{ margin: '0 0 8px', fontWeight: 600, color: '#c62828' }}>Access removed</p>
                    <p style={row}>• Marked as no longer employed — cannot log in</p>
                    <p style={row}>• {plan.will_revoke_tokens || 0} active login session(s) revoked immediately</p>
                    {plan.will_clear_mailbox_credential && <p style={row}>• Saved mailbox password erased</p>}
                    {crews.length > 0 && crews.map((c, i) => (
                      <p key={i} style={row}>• Removed from crew: {c.crew_name || `#${c.crew_id}`}</p>
                    ))}
                  </div>

                  <div style={{ backgroundColor: '#fffde7', border: '1px solid #fff59d', borderRadius: '6px', padding: '15px', marginBottom: '15px' }}>
                    <p style={{ margin: '0 0 8px', fontWeight: 600, color: '#f57f17' }}>
                      Unassigned from {jobs.length} open job{jobs.length === 1 ? '' : 's'}
                    </p>
                    {jobs.length === 0 && <p style={row}>No open jobs.</p>}
                    {jobs.length > 0 && (
                      <div style={{ maxHeight: '160px', overflowY: 'auto' }}>
                        {jobs.map(j => (
                          <p key={j.id} style={row}>• {j.job_number} <span style={{ color: '#888' }}>({j.status})</span></p>
                        ))}
                      </div>
                    )}
                    <p style={{ ...row, color: '#f57f17', marginTop: '10px' }}>
                      Check that someone else is still on these before you confirm.
                    </p>
                  </div>

                  <div style={{ backgroundColor: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: '6px', padding: '15px', marginBottom: '20px' }}>
                    <p style={{ margin: '0 0 8px', fontWeight: 600, color: '#2e7d32' }}>Kept (nothing is erased)</p>
                    <p style={row}>• {plan.preserved?.time_entries ?? 0} time entries — payroll history is untouched</p>
                    <p style={row}>• {plan.preserved?.job_comments ?? 0} job notes</p>
                    <p style={row}>• Their name stays on completed work</p>
                  </div>

                  <p style={{ fontSize: '13px', color: '#888', margin: '0 0 15px' }}>
                    Equipment still checked out to them is not affected — collect that separately.
                  </p>
                </>
              )}

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary" onClick={() => setOffboardPlan(null)} disabled={offboardBusy}>
                  Cancel
                </button>
                {blocking.length === 0 && (
                  <button className="btn" onClick={confirmOffboard} disabled={offboardBusy}
                          style={{ backgroundColor: '#f44336', color: 'white' }}>
                    {offboardBusy ? 'Working…' : `Offboard ${member.name}`}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      <div className="team-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '15px' }}>
        {team.map(member => (
          <div key={member.id} className="team-card" style={{
            backgroundColor: member.is_active === false ? '#fafafa' : 'white',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            border: member.is_active === false ? '1px dashed #bdbdbd' : 'none',
            // Greyed, not hidden: their history is still worth reaching. Kept above 0.5
            // so phone numbers and hours stay readable in daylight on a field tablet.
            opacity: member.is_active === false ? 0.65 : 1,
            filter: member.is_active === false ? 'grayscale(100%)' : 'none'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h3 style={{ margin: '0 0 5px' }}>{member.name}</h3>
                {member.is_active === false && (
                  <p style={{
                    margin: '0 0 8px',
                    display: 'inline-block',
                    backgroundColor: '#e0e0e0',
                    color: '#424242',
                    padding: '3px 10px',
                    borderRadius: '15px',
                    fontSize: '11px',
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase'
                  }}>
                    No Longer Employed
                  </p>
                )}
                {member.role && (
                  <p style={{
                    margin: '0 0 10px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    backgroundColor: hasPayrollAccess(member.role) ? '#e3f2fd' : '#f5f5f5',
                    color: hasPayrollAccess(member.role) ? '#1976d2' : '#666',
                    padding: '4px 10px',
                    borderRadius: '15px',
                    fontSize: '13px'
                  }}>
                    {hasPayrollAccess(member.role) && <FiShield size={12} />}
                    {member.role}
                  </p>
                )}
              </div>
              {/* Only show edit/delete buttons for users with admin permissions */}
              {canManageTeam && (
                <div style={{ display: 'flex', gap: '5px' }}>
                  <button
                    onClick={() => openCareerModal(member)}
                    className="btn-icon"
                    style={{ padding: '5px', background: 'none', border: 'none', cursor: 'pointer', color: '#9c27b0' }}
                    title="Career & Certifications"
                  >
                    <FiAward />
                  </button>
                  <button
                    onClick={() => openCredentialsModal(member)}
                    className="btn-icon"
                    style={{ padding: '5px', background: 'none', border: 'none', cursor: 'pointer', color: '#ff9800' }}
                    title="Manage Login"
                  >
                    <FiKey />
                  </button>
                  <button
                    onClick={() => handleEdit(member)}
                    className="btn-icon"
                    style={{ padding: '5px', background: 'none', border: 'none', cursor: 'pointer' }}
                    title="Edit"
                  >
                    <FiEdit />
                  </button>
                  {member.is_active === false ? (
                    <button
                      onClick={() => handleReactivate(member)}
                      className="btn-icon"
                      style={{ padding: '5px', background: 'none', border: 'none', cursor: 'pointer', color: '#2e7d32' }}
                      title="Reactivate"
                    >
                      <FiRotateCcw />
                    </button>
                  ) : (
                    <button
                      onClick={() => handleOffboard(member)}
                      disabled={offboardBusy || member.id === user?.id}
                      className="btn-icon btn-danger"
                      style={{
                        padding: '5px', background: 'none', border: 'none',
                        cursor: member.id === user?.id ? 'not-allowed' : 'pointer',
                        color: member.id === user?.id ? '#ccc' : '#f44336'
                      }}
                      title={member.id === user?.id ? 'You cannot offboard yourself' : 'Offboard (they have left the company)'}
                    >
                      <FiUserX />
                    </button>
                  )}
                </div>
              )}
            </div>
            {member.career_tracks && member.career_tracks.length > 0 && (
              <div style={{ margin: '8px 0' }}>
                <CareerBadgeList tracks={member.career_tracks} size="small" />
              </div>
            )}
            <div style={{ fontSize: '14px', color: '#666' }}>
              {member.email && <p style={{ margin: '5px 0' }}>📧 {member.email}</p>}
              {member.phone && <p style={{ margin: '5px 0' }}>📱 {member.phone}</p>}
              {canManageTeam && member.hourly_rate > 0 && (
                <p style={{ margin: '5px 0', fontWeight: '500', color: '#4caf50' }}>
                  💵 ${member.hourly_rate.toFixed(2)}/hr
                </p>
              )}
              {member.hire_date && (
                <p style={{ margin: '5px 0' }}>📅 Hired: {new Date(member.hire_date + 'T00:00:00').toLocaleDateString()}</p>
              )}
              {member.birth_month && member.birth_day && (
                <p style={{ margin: '5px 0' }}>🎂 Birthday: {BIRTH_MONTHS[member.birth_month - 1]} {member.birth_day}</p>
              )}
              {member.employment_type && (
                <p style={{ margin: '5px 0' }}>👤 {member.employment_type}</p>
              )}
            </div>
            {member.username && (
              <p style={{ 
                margin: '10px 0 0', 
                fontSize: '12px', 
                color: '#666',
                backgroundColor: '#f5f5f5',
                padding: '5px 10px',
                borderRadius: '4px',
                display: 'inline-block'
              }}>
                🔐 Login: <strong>{member.username}</strong>
              </p>
            )}
            {!member.username && canManageTeam && (
              <p style={{ 
                margin: '10px 0 0', 
                fontSize: '12px', 
                color: '#999',
                fontStyle: 'italic'
              }}>
                No login credentials set
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default Team;